import React, { useEffect, useState } from "react";
import { Modal, Form, Input, InputNumber, Select, DatePicker, Checkbox } from "antd";
import dayjs from "dayjs";
import axios from "axios";

const { Option } = Select;

function CreatePromoCodeModal({ editingPromo, isModalVisible, setIsModalVisible, loading, handleSubmit }) {
  const [form] = Form.useForm();
  const [affiliatePartners, setAffiliatePartners] = useState([]);
  const [loadingPartners, setLoadingPartners] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [showAffiliateAssign, setShowAffiliateAssign] = useState(false);

  useEffect(() => {
    if (editingPromo) {
      const affiliateIds = editingPromo.affiliates?.map((a) => a._id) || [];
      form.setFieldsValue({
        ...editingPromo,
        expiresAt: editingPromo.expiresAt ? dayjs(editingPromo.expiresAt) : null,
      });
    } else {
      form.resetFields();
    }
  }, [editingPromo, form]);

  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 200);

    return () => {
      clearTimeout(timerId);
    };
  }, [searchQuery]);

  useEffect(() => {
    const fetchAffiliatePartners = async () => {
      try {
        setLoadingPartners(true);
        const response = await axios.get(
          `${process.env.REACT_APP_ADMIN_API_URL}affiliate/affiliated-users?search=${encodeURIComponent(debouncedSearchQuery)}`,
          {
            headers: {
              "x-product-type": "PIP_GRID",
              "Content-Type": "application/json",
            },
          }
        );

        if (response.data.success) {
          const partners = response.data.payload.users;
          setAffiliatePartners(partners);
          
          // Only set form values if we're not searching
          if (editingPromo?.affiliates?.length && !debouncedSearchQuery) {
            const affiliateValues = editingPromo.affiliates.map((a) => ({
              value: a._id,
              label: a.fullName,
            }));
            form.setFieldsValue({ assignedAffiliates: affiliateValues.map((a) => a.value) });
          }
        }
      } catch (error) {
        console.error("Error fetching affiliate partners:", error);
      } finally {
        setLoadingPartners(false);
      }
    };

    if (isModalVisible) {
      fetchAffiliatePartners();
    }
  }, [isModalVisible, editingPromo, form, debouncedSearchQuery]);

  const onOk = async () => {
    try {
      const values = await form.validateFields();
      const assignedAffiliates = Array.isArray(values.assignedAffiliates) 
        ? values.assignedAffiliates 
        : values.assignedAffiliates 
          ? [values.assignedAffiliates] 
          : [];
          
      const formattedValues = {
        ...values,
        assignedAffiliates,
        expiresAt: values.expiresAt ? values.expiresAt.format("YYYY-MM-DD") : null,
      };

      if (editingPromo) {
        formattedValues.id = editingPromo._id;
      }

      await handleSubmit(formattedValues);
      form.resetFields();
    } catch (error) {
      console.error("Form validation failed:", error);
    }
  };

  return (
    <Modal
      title={editingPromo ? "Edit Promo Code" : "Create New Promo Code"}
      open={isModalVisible}
      onOk={onOk}
      onCancel={() => {
        form.resetFields();
        setIsModalVisible(false);
      }}
      confirmLoading={loading}
      width={700}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          discountType: "percentage",
          promoUseType: "SINGLE",
          assignedAffiliates: [],
        }}
        style={{ maxWidth: "100%" }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: "16px",
            marginBottom: "16px",
            alignItems: "start",
          }}
        >
          {/* Row 1 */}
          <Form.Item name="code" label="Promo Code" rules={[{ required: true, message: "Please enter promo code!" }]} style={{ margin: 0 }}>
            <Input placeholder="e.g., WELCOME10" style={{ width: "100%", textTransform: "uppercase" }} disabled={!!editingPromo} />
          </Form.Item>
          <Form.Item name="discountType" label="Discount Type" style={{ margin: 0 }}>
            <Select style={{ width: "100%" }}>
              <Option value="percentage">Percentage</Option>
              <Option value="fixed">Fixed Amount</Option>
            </Select>
          </Form.Item>

          {/* Row 2 - Dynamic based on discount type */}
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) =>
              getFieldValue("discountType") === "percentage" ? (
                <Form.Item
                  name="discountAmount"
                  label="Discount Percentage"
                  rules={[
                    { required: true, message: "Please enter discount percentage!" },
                    { type: "number", min: 1, max: 100, message: "Must be between 1 and 100" },
                  ]}
                  style={{ margin: 0 }}
                >
                  <InputNumber
                    min={1}
                    max={100}
                    style={{ width: "100%" }}
                    formatter={(value) => `${value}`}
                    parser={(value) => value ? value.replace(/[^0-9]/g, '') : ''}
                    addonAfter="%"
                    type="number"
                  />
                </Form.Item>
              ) : (
                <Form.Item
                  name="discountAmount"
                  label="Discount Amount"
                  rules={[
                    { required: true, message: "Please enter discount amount!" },
                    // { type: "number", min: 0.01, message: "Must be greater than 0" },
                  ]}
                  style={{ margin: 0 }}
                >
                  <InputNumber min={0.01} prefix="$" style={{ width: "100%" }} precision={2} />
                </Form.Item>
              )
            }
          </Form.Item>
          <Form.Item name="promoUseType" label="Usage Type" style={{ margin: 0 }}>
            <Select style={{ width: "100%" }}>
              <Option value="SINGLE">Single Use</Option>
              <Option value="MULTIPLE">Multiple Use</Option>
            </Select>
          </Form.Item>

          {/* Row 3 */}
          <Form.Item
            name="expiresAt"
            label="Expiry Date"
            rules={[{ required: true, message: "Please select expiry date!" }]}
            style={{ margin: 0 }}
          >
            <DatePicker style={{ width: "100%" }} disabledDate={(current) => current && current < dayjs().startOf("day")} format="DD-MM-YYYY" />
          </Form.Item>
          <div className="label-block checkbox-flex">
            <Form.Item
              name="assignedAffiliates"
              label={
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
                  <div>
                    <span>Assign to Affiliates</span>
                  </div>
                  <Checkbox
                    checked={showAffiliateAssign}
                    onChange={(e) => {
                      setShowAffiliateAssign(e.target.checked);
                      if (!e.target.checked) {
                        form.setFieldsValue({ assignedAffiliates: [] });
                      }
                    }}
                    style={{
                      "--antd-checkbox-size": "22px",
                      transform: "scale(1.1)",
                      display: "flex",
                      margin: 0,
                      padding: 0,
                    }}
                  />
                </div>
              }
              style={{ display: "block" }}
            >
              {showAffiliateAssign && (
                <Select
                  placeholder="Select affiliates"
                  loading={loadingPartners}
                  filterOption={false}
                  onSearch={(value) => setSearchQuery(value)}
                  onFocus={() => setSearchQuery("")}
                  style={{ width: "100%" }}
                  showSearch
                  allowClear
                >
                  {affiliatePartners.map((partner) => (
                    <Option key={partner._id} value={partner._id}>
                      {partner.fullName}
                    </Option>
                  ))}
                </Select>
              )}
            </Form.Item>
          </div>
        </div>
      </Form>
    </Modal>
  );
}

export default CreatePromoCodeModal;
